import React, { useState, useRef, useEffect } from 'react';
import { 
  Grid, Package, Settings, Edit3, ChevronRight, ChevronLeft, Heart, LogOut,
  Shield, Lock, Smartphone, Mail, HelpCircle, Laptop,
  Flag, Share2, UserPlus, Users, Palette, Activity, Coins, 
  Ghost, Globe, Check, Search, User, Layers, Loader2, Slash, 
  Trash2, Archive, MessageCircle, X, ShoppingBag, CheckCircle, ArrowLeft, Play, Bookmark, Send,
  Tag, MapPin, Clock, DollarSign, MessageSquare, AlertCircle, Star,
  Target, QrCode, Zap
} from 'lucide-react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth, db, storage } from '../../services/firebase';
import { Product, Order, UserProfile } from '../../types';
import { doc, getDoc, collection, query, where, onSnapshot, deleteDoc, updateDoc, arrayRemove, arrayUnion, addDoc, serverTimestamp, increment, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';

import { Wallet } from '../settings/Wallet';
import { WalletTerminal } from '../wallet/WalletTerminal';
import { AffiliateModal } from '../settings/AffiliateModal';

// ============================================================================
// HELPERS
// ============================================================================
const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Recently';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
};

const AuthorInfo = ({ authorId, fallbackName, fallbackImage, onClick, onDelete, isOwner }: any) => {
    const [author, setAuthor] = useState({ name: fallbackName || 'User', image: fallbackImage });

    useEffect(() => {
        if (!authorId) return;
        const fetchAuthor = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'users', authorId));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setAuthor({ name: data.displayName || data.username || fallbackName || 'User', image: data.photoURL || data.photoUrl || data.avatar || fallbackImage });
                }
            } catch (e) { console.error(e); }
        };
        fetchAuthor();
    }, [authorId, fallbackName, fallbackImage]);

    return (
        <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3 text-left group flex-1 cursor-pointer" onClick={onClick}>
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full lg:rounded-none bg-zinc-900 flex items-center justify-center text-white font-bold text-sm overflow-hidden transition-transform group-active:scale-95 shrink-0 border border-zinc-800">
                    {author.image ? <img src={author.image} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale" alt="Author"/> : <User size={18}/>}
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-black text-white uppercase tracking-widest leading-none truncate group-hover:underline underline-offset-4">{author.name}</p>
                    <p className="text-[10px] text-zinc-500 mt-1 font-mono uppercase tracking-widest">Network</p>
                </div>
            </div>
            {isOwner && (
                <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-2 text-zinc-600 hover:text-red-500 transition-colors rounded-full lg:rounded-none">
                    <Trash2 size={16} />
                </button>
            )}
        </div>
    );
};

const ImageCarousel = ({ images, title, onDoubleTap }: { images: string[], title: string, onDoubleTap?: () => void }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showStar, setShowStar] = useState(false);
    const imageList = (images && images.length > 0) ? images : ["https://placehold.co/800x800/111/444?text=NO+SIGNAL"];

    const handleScroll = () => {
        if (scrollRef.current) {
            const index = Math.round(scrollRef.current.scrollLeft / scrollRef.current.clientWidth);
            setCurrentIndex(index);
        }
    };

    const scrollTo = (index: number) => {
        if (scrollRef.current) scrollRef.current.scrollTo({ left: index * scrollRef.current.clientWidth, behavior: 'smooth' });
    };

    const handleDoubleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onDoubleTap) {
            setShowStar(true);
            onDoubleTap();
            setTimeout(() => setShowStar(false), 1000);
        }
    };

    return (
        <div className="relative w-full h-full group bg-zinc-950 overflow-hidden cursor-pointer" onDoubleClick={handleDoubleClick}>
            <div ref={scrollRef} onScroll={handleScroll} className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide">
                {imageList.map((img, i) => (
                    <img key={i} src={img} className="w-full h-full object-cover shrink-0 snap-center select-none grayscale group-hover:grayscale-0 transition-all duration-700" alt={`${title} ${i + 1}`} />
                ))}
            </div>

            {showStar && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30 animate-in zoom-in duration-300 fade-out delay-500">
                    <Star size={120} className="text-white fill-white drop-shadow-[0_0_40px_rgba(0,0,0,0.5)]" />
                </div>
            )}

            {imageList.length > 1 && (
                <>
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5 z-20 pointer-events-none">
                        {imageList.map((_, idx) => (<div key={idx} className={`h-1.5 transition-all ${idx === currentIndex ? 'bg-white w-6 rounded-full lg:rounded-none' : 'bg-white/40 w-1.5 rounded-full lg:rounded-none'}`} />))}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); scrollTo(currentIndex > 0 ? currentIndex - 1 : imageList.length - 1); }} className="hidden lg:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 text-white items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border border-white/20 backdrop-blur-md hover:bg-white hover:text-black rounded-none"><ChevronLeft size={20}/></button>
                    <button onClick={(e) => { e.stopPropagation(); scrollTo(currentIndex < imageList.length - 1 ? currentIndex + 1 : 0); }} className="hidden lg:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 text-white items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border border-white/20 backdrop-blur-md hover:bg-white hover:text-black rounded-none"><ChevronRight size={20}/></button>
                </>
            )}
        </div>
    );
};

// ============================================================================
// 1. UNIFIED POST DETAIL MODAL 
// ============================================================================
const PostDetailModal = ({ post, isOpen, onClose, onSelectProduct, currentUser, onSave, allPosts }: any) => {
    const [comments, setComments] = useState<any[]>([]);
    const [newComment, setNewComment] = useState('');
    const [loadingAction, setLoadingAction] = useState(false);
    const [copied, setCopied] = useState(false);
    
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [showHeart, setShowHeart] = useState(false);

    useEffect(() => {
        if (!post || !isOpen) return;
        const q = query(collection(db, "posts", post.id, "comments"), orderBy("createdAt", "asc"));
        const unsubscribe = onSnapshot(q, (snap) => {
            setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsubscribe();
    }, [post, isOpen]);

    const handleDelete = async () => {
        if (!window.confirm("Delete this post?")) return;
        setLoadingAction(true);
        try { await deleteDoc(doc(db, "posts", post.id)); onClose(); } catch (e) { console.error(e); }
        setLoadingAction(false);
    };

    const handleArchive = async () => {
        if (!window.confirm("Archive this post?")) return;
        setLoadingAction(true);
        try { await updateDoc(doc(db, "posts", post.id), { archived: true }); onClose(); } catch (e) { console.error(e); }
        setLoadingAction(false);
    };

    const handleShare = async () => {
        if (navigator.share) {
            try { await navigator.share({ title: `Check out my post`, url: `https://scoop.app/post/${post.id}` }); } 
            catch (err) { console.log("Share cancelled"); }
        } else {
            navigator.clipboard.writeText(`https://scoop.app/post/${post.id}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleLikeToggle = async () => {
        if (!currentUser?.uid || !post?.id) return;
        const isLiked = post.likes?.includes(currentUser.uid);
        const postRef = doc(db, "posts", post.id);
        try {
            if (isLiked) {
                await updateDoc(postRef, { likes: arrayRemove(currentUser.uid) });
            } else {
                await updateDoc(postRef, { likes: arrayUnion(currentUser.uid) });
            }
        } catch (e) { console.error("Like failed", e); }
    };

    const handleSendComment = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!newComment.trim() || !currentUser?.uid || !post?.id) return;
        setLoadingAction(true);
        try {
            await addDoc(collection(db, "posts", post.id, "comments"), {
                text: newComment,
                userId: currentUser.uid,
                userName: currentUser.displayName || 'User',
                userAvatar: currentUser.photoURL || currentUser.avatar || '',
                createdAt: serverTimestamp()
            });
            await updateDoc(doc(db, "posts", post.id), { comments: increment(1) });
            setNewComment('');
        } catch (error) { console.error(error); }
        setLoadingAction(false);
    };

    const handleDeleteComment = async (commentId: string) => {
        if (!window.confirm("Delete your reply?")) return;
        try {
            await deleteDoc(doc(db, "posts", post.id, "comments", commentId));
            await updateDoc(doc(db, "posts", post.id), { comments: increment(-1) });
        } catch (e) { console.error("Error deleting comment", e); }
    };

    const handleSingleClick = (e: React.MouseEvent) => {
        if (e.detail === 1) {
            if (videoRef.current?.paused) {
                videoRef.current.play();
                setIsPlaying(true);
            } else {
                videoRef.current?.pause();
                setIsPlaying(false);
            }
        }
    };

    if (!isOpen || !post) return null;
    
    const isVideo = post.contentType === 'hook' || post.videoUrl || post.video;
    const displayMedia = isVideo ? (post.videoUrl || post.video) : (post.image || (post.images && post.images[0]) || post.imageUrl || post.img);
    let items = post.outfitItems || post.items || post.taggedItems || [];
    if (items.length === 0 && post.taggedProductName) {
        items = [{ id: post.taggedProductId || Math.random().toString(), name: post.taggedProductName, image: post.taggedProductImage || post.image, price: post.taggedProductPrice || 0, brand: 'Tagged Item' }];
    }
    const validItems = items.filter((i: any) => i && (i.name || i.image));
    const isOwner = currentUser?.uid === post?.authorId || currentUser?.uid === post?.userId || currentUser?.uid === post?.uid;
    const isSaved = currentUser?.savedPosts?.includes(post.id);



    // 🟢 2. TWITTER-STYLE SINGLE COLUMN MODAL (FOR TEXT POSTS)
    if (post.type === 'thought') {
        return (
            <div className="fixed inset-0 z-[800] bg-black/90 backdrop-blur-md flex justify-center items-center animate-in fade-in p-4 sm:p-6 overflow-y-auto">
                <div className="w-full max-w-xl bg-black border border-zinc-800 rounded-2xl flex flex-col max-h-full shadow-2xl relative my-auto" onClick={(e) => e.stopPropagation()}>
                    <button onClick={onClose} className="absolute top-4 right-4 p-2 text-zinc-500 hover:text-white transition-colors z-10 bg-black rounded-full border border-zinc-800 hover:border-zinc-600">
                        <X size={20} />
                    </button>
                    
                    <div className="flex-1 overflow-y-auto scrollbar-hide">
                        <div className="p-6 sm:p-8 border-b border-zinc-900">
                            <div className="flex items-center justify-between mb-6 pr-10">
                                <AuthorInfo 
                                    authorId={post.authorId || post.userId || post.uid} 
                                    fallbackName={post.authorName || post.userName} 
                                    fallbackImage={post.authorImage || post.userAvatar} 
                                    isOwner={isOwner}
                                    onDelete={handleDelete}
                                />
                            </div>
                            
                            <p className="text-xl sm:text-2xl font-medium text-white mb-6 leading-relaxed break-words whitespace-pre-wrap font-sans">{post.caption}</p>
                            
                            <div className="flex gap-6 py-4 border-y border-zinc-800 text-zinc-500 text-sm font-bold">
                                <span><strong className="text-white">{post.likes?.length || 0}</strong> Likes</span>
                                <span><strong className="text-white">{post.comments || 0}</strong> Replies</span>
                            </div>

                            <div className="flex justify-between py-2 border-b border-zinc-900">
                                <button onClick={handleLikeToggle} className="p-3 text-zinc-500 hover:text-white transition-colors flex items-center gap-2 group">
                                    <Heart size={20} className={post.likes?.includes(currentUser?.uid) ? "fill-white text-red-500" : "group-hover:text-white"} />
                                </button>
                                <button className="p-3 text-zinc-500 hover:text-white transition-colors cursor-default">
                                    <MessageCircle size={20} />
                                </button>
                                <button onClick={() => onSave(post.id, 'post')} className="p-3 text-zinc-500 hover:text-white transition-colors">
                                    <Bookmark size={20} fill={isSaved ? "white" : "none"} className={isSaved ? "text-white" : ""} />
                                </button>
                                <button onClick={handleShare} className="p-3 text-zinc-500 hover:text-white transition-colors">
                                    {copied ? <CheckCircle size={20} className="text-green-500"/> : <Share2 size={20} />}
                                </button>
                            </div>

                            <div className="mt-6 space-y-6">
                                {comments.length > 0 ? comments.map((c, i) => (
                                    <div key={i} className="flex gap-4 group">
                                        <div className="w-10 h-10 rounded-full bg-zinc-900 overflow-hidden shrink-0 border border-zinc-800 flex items-center justify-center">
                                            {c.userAvatar ? <img src={c.userAvatar} referrerPolicy="no-referrer" className="w-full h-full object-cover"/> : <User size={18} className="text-zinc-500"/>}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center justify-between">
                                                <p className="text-white font-bold text-sm">{c.userName || 'User'}</p>
                                                {c.userId === currentUser?.uid && (
                                                    <button onClick={() => handleDeleteComment(c.id)} className="text-zinc-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <Trash2 size={12} />
                                                    </button>
                                                )}
                                            </div>
                                            <p className="text-zinc-300 text-sm mt-1 leading-relaxed font-sans">{c.text}</p>
                                        </div>
                                    </div>
                                )) : <p className="text-zinc-600 text-sm font-mono uppercase tracking-widest text-center py-6 border border-dashed border-zinc-900 rounded-xl">Be the first to reply</p>}
                            </div>
                        </div>
                    </div>
                    
                    <div className="p-4 border-t border-zinc-800 bg-zinc-950 shrink-0 rounded-b-2xl">
                        <form onSubmit={handleSendComment} className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-zinc-800 overflow-hidden shrink-0 border border-zinc-700 flex items-center justify-center">
                                {currentUser?.photoURL || currentUser?.avatar ? <img src={currentUser.photoURL || currentUser.avatar} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale" /> : <User size={16} className="text-zinc-500"/>}
                            </div>
                            <input value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Post your reply..." className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-zinc-600 font-sans" />
                            <button type="submit" disabled={!newComment.trim() || loadingAction} className="px-5 py-2 bg-white text-black font-black uppercase tracking-widest text-[10px] rounded-full disabled:opacity-50 hover:bg-zinc-200 transition-colors">
                                {loadingAction ? <Loader2 size={14} className="animate-spin"/> : 'Reply'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    // 🟢 3. STANDARD MODAL (FOR IMAGES/VIDEOS)
    return (
        <div className="fixed inset-0 z-[200] bg-black flex flex-col lg:flex-row animate-in fade-in duration-300">
            {/* Left: Media */}
            <div className="w-full lg:w-[60%] h-[50vh] lg:h-full bg-zinc-950 border-b lg:border-b-0 lg:border-r border-zinc-800 relative flex items-center justify-center overflow-hidden">
                <button onClick={onClose} className="absolute top-6 left-6 z-50 p-3 bg-black/50 hover:bg-white hover:text-black text-white rounded-full lg:rounded-none backdrop-blur-md transition-colors border border-white/20">
                    <ArrowLeft size={24}/>
                </button>

                {isVideo ? (
                    <div className="relative w-full h-full cursor-pointer group" onClick={handleSingleClick}>
                        <video 
                            ref={videoRef}
                            src={displayMedia} 
                            className="w-full h-full object-contain bg-black" 
                            autoPlay 
                            loop 
                            playsInline 
                        />
                        {!isPlaying && (
                             <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/10">
                                 <div className="w-20 h-20 bg-black/50 backdrop-blur-md rounded-full lg:rounded-none flex items-center justify-center text-white border border-white/20">
                                     <div className="w-0 h-0 border-t-[10px] border-t-transparent border-l-[16px] border-l-white border-b-[10px] border-b-transparent ml-2"></div>
                                 </div>
                             </div>
                        )}
                    </div>
                ) : (
                    <div className="relative w-full h-full cursor-pointer">
                        <ImageCarousel images={post.images || [displayMedia]} title={post.title || post.caption} />
                    </div>
                )}
            </div>
            
            {/* Right: Content */}
            <div className="w-full lg:w-[40%] flex flex-col h-[50vh] lg:h-full bg-black">
                <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
                    <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter">Post Details</h2>
                    {isOwner && (
                        <div className="flex gap-2">
                            <button onClick={handleArchive} disabled={loadingAction} className="p-2 text-zinc-500 hover:text-white transition-colors">{loadingAction ? <Loader2 className="animate-spin" size={20}/> : <Archive size={20}/>}</button>
                            <button onClick={handleDelete} disabled={loadingAction} className="p-2 text-zinc-500 hover:text-red-500 transition-colors">{loadingAction ? <Loader2 className="animate-spin" size={20}/> : <Trash2 size={20}/>}</button>
                        </div>
                    )}
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-8 scrollbar-hide">
                    <div>
                        <h3 className="text-xl font-black text-white uppercase tracking-tight mb-2">{post.title || "Fit Check"}</h3>
                        <p className="text-zinc-400 text-sm leading-relaxed">{post.description || post.caption}</p>
                    </div>

                    <div className="flex justify-between items-center py-4 border-y border-zinc-800">
                        <div className="flex items-center gap-6">
                            <button onClick={handleLikeToggle} className="flex items-center gap-2 group">
                                <Heart size={20} className={`transition-colors ${post.likes?.includes(currentUser?.uid) ? 'text-white fill-white' : 'text-zinc-500 group-hover:text-white'}`}/>
                                <span className="text-white font-bold text-sm">{(post.likes || []).length}</span>
                            </button>
                            <div className="flex items-center gap-2"><MessageCircle size={20} className="text-zinc-500"/><span className="text-white font-bold text-sm">{comments.length}</span></div>
                        </div>
                        <button onClick={handleShare} className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-xs font-bold uppercase tracking-widest">
                            {copied ? <CheckCircle size={16} className="text-green-500"/> : <Share2 size={16}/>}
                            {copied ? "Copied" : "Share"}
                        </button>
                    </div>

                    {validItems.length > 0 && (
                        <div>
                            <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4 flex items-center gap-2"><ShoppingBag size={14}/> Shop this Fit</h3>
                            <div className="grid grid-cols-2 gap-4">
                                {validItems.map((item: any, idx: number) => (
                                    <button key={idx} onClick={() => { onClose(); onSelectProduct(item); }} className="text-left group border border-zinc-800 p-2 rounded-xl lg:rounded-none hover:border-white transition-colors">
                                        <div className="w-full aspect-square bg-zinc-900 overflow-hidden mb-2 relative rounded-lg lg:rounded-none">
                                            <img src={item.image} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"/>
                                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-black tracking-widest uppercase">View</div>
                                        </div>
                                        <p className="text-white text-[10px] font-bold truncate mb-0.5">{item.name}</p>
                                        <p className="text-zinc-500 text-[9px] uppercase tracking-wider truncate">{item.brand}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="pb-6">
                        <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4 flex items-center gap-2"><MessageCircle size={14}/> Comments</h3>
                        <div className="space-y-6">
                            {comments.length > 0 ? comments.map((c, i) => (
                                <div key={i} className="flex gap-3 group">
                                    <div className="w-8 h-8 rounded-full bg-zinc-900 overflow-hidden shrink-0 border border-zinc-800 flex items-center justify-center">
                                        {c.userAvatar ? <img src={c.userAvatar} referrerPolicy="no-referrer" className="w-full h-full object-cover"/> : <User size={16} className="text-zinc-500"/>}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <span className="font-bold text-white text-sm">{c.userName || 'User'}</span>
                                            {c.userId === currentUser?.uid && (
                                                <button onClick={() => handleDeleteComment(c.id)} className="text-zinc-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-zinc-400 text-sm mt-1">{c.text}</p>
                                    </div>
                                </div>
                            )) : <p className="text-zinc-600 text-xs italic">No comments yet.</p>}
                        </div>
                    </div>
                </div>

                <div className="p-4 border-t border-zinc-800 bg-zinc-950 shrink-0">
                    <form onSubmit={handleSendComment} className="flex items-center gap-3">
                        <input value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment..." className="flex-1 bg-black border border-zinc-800 rounded-full lg:rounded-none px-4 py-3 text-sm text-white outline-none focus:border-white transition-colors" />
                        <button type="submit" disabled={!newComment.trim() || loadingAction} className="p-3 bg-white text-black rounded-full lg:rounded-none disabled:opacity-50 hover:bg-zinc-200 transition-colors">
                            {loadingAction ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// 2. MAIN PROFILE VIEW
// ============================================================================

interface ProfileViewProps {
  profile: UserProfile;
  products: Product[];
  orders: Order[];
  wishlist: any[];
  followingBrands: string[];
  onEditProfile: () => void;
  onBuyCredits?: () => void; 
  onInviteFriends: () => void;
  onSelectProduct: (product: Product) => void;
  onViewBrand: (brandName: string) => void;
  onViewProfile?: (userId: string) => void;
  onTrackOrder: (order: Order) => void;
  onUpdateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  onSignOut: () => void;
  onOpenSettings?: () => void;
}

export const ProfileView: React.FC<ProfileViewProps> = (props) => {
  const { profile, orders = [], products, wishlist = [], followingBrands, onEditProfile, onInviteFriends, onSelectProduct, onViewBrand, onViewProfile, onTrackOrder, onUpdateProfile, onSignOut } = props;
  const navigate = useNavigate();

  const [activeView, setActiveView] = useState<'closet' | 'saved' | 'orders' | 'wishlist' | 'settings' | 'following' | 'followers'>('closet');
  const [settingsSubView, setSettingsSubView] = useState<'menu' | 'phone' | 'email' | 'style' | 'payment' | 'activity' | 'blocked' | 'privacy' | 'privacy-confirm'>('menu');
  const [myPosts, setMyPosts] = useState<any[]>([]);
  const [savedPosts, setSavedPosts] = useState<any[]>([]); 
  const [followersList, setFollowersList] = useState<any[]>([]);
  const [followingUsersList, setFollowingUsersList] = useState<any[]>([]);
  const [followingBrandsList, setFollowingBrandsList] = useState<any[]>([]); 
  const [blockedList, setBlockedList] = useState<any[]>([]);
  const [selectedPost, setSelectedPost] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [listSearchTerm, setListSearchTerm] = useState(''); 
  const [pendingPrivacy, setPendingPrivacy] = useState<'public' | 'private' | 'ghost' | null>(null);
  const [tempInput, setTempInput] = useState('');
  const [selectedStyles, setSelectedStyles] = useState<string[]>(profile?.stylePreferences || []);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isWalletOpen, setIsWalletOpen] = useState(false);
  const [isAffiliateOpen, setIsAffiliateOpen] = useState(false);


  useEffect(() => {
    if (!profile?.uid) return;
    const postsMap = new Map(); 
    const updatePosts = () => {
        const uniquePosts = Array.from(postsMap.values()).sort((a: any, b: any) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setMyPosts(uniquePosts);
        setLoadingPosts(false);
    };
    const q1 = query(collection(db, "posts"), where("authorId", "==", profile.uid));
    const unsub1 = onSnapshot(q1, (snap) => { snap.docs.forEach(doc => postsMap.set(doc.id, { id: doc.id, ...doc.data() })); updatePosts(); });
    const q2 = query(collection(db, "posts"), where("userId", "==", profile.uid));
    const unsub2 = onSnapshot(q2, (snap) => { snap.docs.forEach(doc => postsMap.set(doc.id, { id: doc.id, ...doc.data() })); updatePosts(); });
    return () => { unsub1(); unsub2(); };
  }, [profile.uid]);

  useEffect(() => {
      if (activeView === 'saved') {
          const fetchSaved = async () => {
              setLoadingPosts(true);
              try {
                  const userDoc = await getDoc(doc(db, 'users', profile.uid));
                  const latestProfile = userDoc.data();
                  const savedIds = latestProfile?.savedPosts || [];

                  if (savedIds.length === 0) {
                      setSavedPosts([]);
                      setLoadingPosts(false);
                      return;
                  }

                  const postsData = await Promise.all(savedIds.map(async (id: string) => {
                      const snap = await getDoc(doc(db, 'posts', id));
                      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
                  }));
                  
                  setSavedPosts(postsData.filter(p => p !== null).reverse()); // newest first
              } catch (e) { 
                  console.error("Error fetching saved posts:", e); 
              }
              setLoadingPosts(false);
          };
          fetchSaved();
      }
  }, [activeView, profile.uid]);

  useEffect(() => {
    if (activeView === 'followers') {
        const followers = profile.followers || [];
        if (followers.length === 0) { setFollowersList([]); return; }
        setLoadingList(true);
        const fetchFollowers = async () => {
            try {
                const usersData = await Promise.all(followers.slice(0, 50).map(async (uid) => {
                    const snap = await getDoc(doc(db, 'users', uid));
                    return snap.exists() ? { uid: snap.id, ...snap.data() } : null;
                }));
                setFollowersList(usersData.filter(u => u !== null));
            } catch (e) { console.error(e); }
            setLoadingList(false);
        };
        fetchFollowers();
    }
  }, [activeView, profile.followers]);

  useEffect(() => {
    if (activeView === 'following') {
        const followingUsers = profile.followingUsers || [];
        const followingBrandIds = profile.followingBrands || [];
        
        setLoadingList(true);
        
        const fetchNetwork = async () => {
            try {
                const usersData = await Promise.all(followingUsers.slice(0, 50).map(async (uid) => {
                    const snap = await getDoc(doc(db, 'users', uid));
                    return snap.exists() ? { uid: snap.id, ...snap.data() } : null;
                }));
                setFollowingUsersList(usersData.filter(u => u !== null));

                const brandsData = await Promise.all(followingBrandIds.slice(0, 50).map(async (brandId) => {
                    const snap = await getDoc(doc(db, 'users', brandId));
                    return snap.exists() ? { uid: snap.id, ...snap.data() } : { uid: brandId, displayName: brandId, isFallback: true };
                }));
                setFollowingBrandsList(brandsData.filter(b => b !== null));

            } catch (e) { console.error(e); }
            setLoadingList(false);
        };
        
        fetchNetwork();
    }
  }, [activeView, profile.followingUsers, profile.followingBrands]);

  useEffect(() => {
    if (settingsSubView === 'blocked') {
        const blockedIds = profile.blockedUsers || [];
        if (blockedIds.length === 0) { setBlockedList([]); return; }
        const fetchBlocked = async () => {
            try {
                const usersData = await Promise.all(blockedIds.map(async (uid: string) => {
                    const snap = await getDoc(doc(db, 'users', uid));
                    return snap.exists() ? { uid: snap.id, ...snap.data() } : { uid, displayName: 'Unknown User' };
                }));
                setBlockedList(usersData);
            } catch (e) { console.error(e); }
        };
        fetchBlocked();
    }
  }, [settingsSubView, profile.blockedUsers]);

  const handleSaveItem = async (itemId: string, type: 'post') => {
      if (!profile?.uid) return;
      const userRef = doc(db, "users", profile.uid);
      const currentArray = profile?.savedPosts || [];
      try {
          if (currentArray.includes(itemId)) {
              await updateDoc(userRef, { savedPosts: arrayRemove(itemId) });
          } else {
              await updateDoc(userRef, { savedPosts: arrayUnion(itemId) });
          }
      } catch(e) { console.error(e); }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    try {
      const storageRef = ref(storage, `avatars/${profile.uid}/${Date.now()}.jpg`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      await onUpdateProfile({ avatar: downloadURL, photoURL: downloadURL });
    } catch (error) {
      console.error("Image upload failed:", error);
      alert("Failed to update profile picture.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSetting = async (field: keyof UserProfile) => { if (!tempInput) return; setLoading(true); try { await onUpdateProfile({ [field]: tempInput }); setSettingsSubView('menu'); setTempInput(''); } catch (error) { console.error(error); } setLoading(false); };
  const handleSaveStyles = async () => { setLoading(true); try { await onUpdateProfile({ stylePreferences: selectedStyles }); setSettingsSubView('menu'); } catch (error) { console.error(error); } setLoading(false); };
  const confirmPrivacyChange = async () => { if (!pendingPrivacy) return; setLoading(true); try { const updates = pendingPrivacy === 'public' ? { isPrivate: false, isAnonymous: false } : pendingPrivacy === 'private' ? { isPrivate: true, isAnonymous: false } : { isPrivate: true, isAnonymous: true }; await onUpdateProfile(updates); setPendingPrivacy(null); setSettingsSubView('menu'); } catch (error) { console.error(error); } setLoading(false); };
  const handleUnblock = async (uidToUnblock: string) => { if (!window.confirm("Unblock?")) return; const currentBlocked = profile.blockedUsers || []; const newBlocked = currentBlocked.filter(id => id !== uidToUnblock); await onUpdateProfile({ blockedUsers: newBlocked }); setBlockedList(prev => prev.filter(u => u.uid !== uidToUnblock)); };
  const handleImageClick = () => fileInputRef.current?.click();
  const handleShareProfile = async () => { try { await navigator.share({ title: `Check out ${profile?.displayName} on Scoop`, url: window.location.href }); } catch (err) { navigator.clipboard.writeText(window.location.href); alert("Profile link copied!"); } };
  const handlePasswordReset = async () => { if (!profile?.email) return; if (window.confirm(`Send password reset email to ${profile.email}?`)) { try { await sendPasswordResetEmail(auth, profile.email); alert("Reset email sent!"); } catch (e: any) { alert("Error: " + e.message); } } };
  const getCurrentLevel = () => { if (profile.isAnonymous) return 'ghost'; if (profile.isPrivate) return 'private'; return 'public'; };
  const privacyDetails = { public: { title: "Switch to Public", icon: Globe, desc: "Visible to everyone." }, private: { title: "Switch to Private", icon: Lock, desc: "Only friends see activity." }, ghost: { title: "Ghost Mode", icon: Ghost, desc: "Completely invisible." } };



  const SettingsRow = ({ icon: Icon, label, value, onClick, isDestructive = false }: any) => ( 
    <button onClick={onClick} className="w-full p-4 flex items-center justify-between hover:bg-zinc-900 transition-colors border-b border-zinc-800 last:border-0">
        <div className="flex items-center gap-4">
            <Icon size={18} className={isDestructive ? "text-red-500" : "text-zinc-500"} />
            <span className={`text-sm font-bold uppercase tracking-widest ${isDestructive ? "text-red-500" : "text-white"}`}>{label}</span>
        </div>
        <div className="flex items-center gap-3">
            {value && <span className="text-xs text-zinc-500 font-mono truncate max-w-[120px]">{value}</span>}
            <ChevronRight size={16} className="text-zinc-600" />
        </div>
    </button> 
  );
  
  const SectionTitle = ({ title }: { title: string }) => <div className="px-6 pt-8 pb-3 text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500">{title}</div>;

  // 🟢 UNIVERSAL GRID RENDERER FOR POSTS (Handles Images, Video, Text, Thrifts)
  const renderGridItems = (posts: any[]) => {
      return posts.map((post) => {
          const isVideo = post.contentType === 'hook' || post.videoUrl || post.video;
          const mediaSrc = isVideo ? (post.videoUrl || post.video) : (post.image || (post.images && post.images[0]) || post.imageUrl || post.img);

          // 🟢 TEXT TWEET-STYLE BLOCK PREVIEW (FIXED)
          if (post.type === 'thought') {
              return (
                  <div key={post.id} onClick={() => setSelectedPost(post)} className="aspect-square relative group overflow-hidden border border-zinc-900 lg:border-zinc-800 lg:rounded-none cursor-pointer bg-black p-3 sm:p-4 flex flex-col hover:bg-zinc-950 transition-colors">
                      <div className="flex items-center gap-2 mb-2 sm:mb-3">
                          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-zinc-900 overflow-hidden shrink-0 border border-zinc-800 flex items-center justify-center">
                              {post.authorImage || post.userAvatar || profile.photoURL ? <img src={post.authorImage || post.userAvatar || profile.photoURL} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale" /> : <User size={12} className="text-zinc-500"/>}
                          </div>
                          <div className="flex flex-col min-w-0">
                              <span className="text-white font-bold text-[10px] sm:text-xs leading-none uppercase tracking-wider truncate">{post.authorName || post.userName || profile.displayName || 'User'}</span>
                          </div>
                      </div>
                      
                      <p className="text-zinc-300 font-sans text-xs sm:text-sm leading-snug line-clamp-4 sm:line-clamp-5 break-words whitespace-pre-wrap">{post.caption}</p>
                      
                      <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity hidden lg:flex items-center justify-center gap-6 text-white font-black z-20">
                          <div className="flex items-center gap-2"><Heart size={20} className={post.likes?.includes(profile.uid) ? "text-white fill-white" : ""}/> {post.likes?.length || 0}</div>
                          <div className="flex items-center gap-2"><MessageCircle size={20}/> {post.comments || 0}</div>
                      </div>
                  </div>
              );
          }

          // 🟢 STANDARD MEDIA / THRIFT PREVIEW
          return (
              <div key={post.id} onClick={() => setSelectedPost(post)} className="aspect-square bg-zinc-950 relative group overflow-hidden border border-zinc-900 lg:border-zinc-800 lg:rounded-none cursor-pointer" >
                  {isVideo ? (
                      <>
                          <video src={mediaSrc} className="w-full h-full object-cover transition-all duration-500 grayscale group-hover:grayscale-0 lg:group-hover:scale-105 pointer-events-none" muted loop playsInline autoPlay />
                          <div className="absolute top-2 right-2 bg-black/80 backdrop-blur-md rounded-none px-1.5 py-1 border border-zinc-800">
                              <Play size={14} className="text-white"/>
                          </div>
                      </>
                  ) : (
                      <>
                          <img src={mediaSrc} className="w-full h-full object-cover transition-all duration-500 grayscale hover:grayscale-0 lg:group-hover:scale-105" alt="Fit" />
                          {post.images && post.images.length > 1 && (
                              <div className="absolute top-2 right-2 bg-black/80 backdrop-blur-md rounded-none px-1.5 py-1 border border-zinc-800">
                                  <Layers size={14} className="text-white"/>
                              </div>
                          )}
                      </>
                  )}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity hidden lg:flex items-center justify-center gap-6 text-white font-black z-20">
                      <div className="flex items-center gap-2"><Heart size={20} className={post.likes?.includes(profile.uid) ? "text-white fill-white" : ""}/> {post.likes?.length || 0}</div>
                  </div>
              </div>
          );
      });
  };

  const renderClosetView = () => {
    if (loadingPosts) return <div className="p-32 flex justify-center"><Loader2 className="animate-spin text-zinc-500" size={32} /></div>;
    if (myPosts.length === 0) return (<div className="py-32 flex flex-col items-center justify-center text-zinc-600 opacity-50"><Grid size={64} strokeWidth={1} className="mb-6" /><p className="text-xl font-black uppercase tracking-[0.3em]">Closet Empty</p></div>);
    
    return (
        <div className="animate-in fade-in duration-500 pb-20 lg:pb-0">
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1 lg:gap-4 p-1 lg:p-6">
                {renderGridItems(myPosts)}
            </div>
        </div>
    );
  };

  const renderSavedPostsView = () => {
    if (loadingPosts) return <div className="p-32 flex justify-center"><Loader2 className="animate-spin text-zinc-500" size={32} /></div>;
    if (savedPosts.length === 0) return (<div className="py-32 flex flex-col items-center justify-center text-zinc-600 opacity-50"><Bookmark size={64} strokeWidth={1} className="mb-6" /><p className="text-xl font-black uppercase tracking-[0.3em]">Vault Empty</p></div>);
    
    return (
        <div className="animate-in fade-in duration-500 pb-20 lg:pb-0">
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1 lg:gap-4 p-1 lg:p-6">
                {renderGridItems(savedPosts)}
            </div>
        </div>
    );
  };

  const renderOrdersView = () => (
    <div className="p-4 lg:p-8 space-y-4 lg:space-y-6 max-w-4xl mx-auto pb-20 lg:pb-8">
        {orders.length > 0 ? orders.map(o => (
            <div key={o.id} className="p-6 lg:p-8 rounded-2xl lg:rounded-none bg-zinc-950 border border-zinc-800 animate-in slide-in-from-bottom-2">
                <div className="flex flex-col lg:flex-row gap-6 mb-6">
                    <div className="w-full lg:w-32 h-32 lg:h-40 rounded-xl lg:rounded-none bg-black overflow-hidden border border-zinc-800 shrink-0">
                        {o.items?.[0] && <img src={o.items[0].image} className="w-full h-full object-cover grayscale"/>}
                    </div>
                    <div className="flex-1 flex flex-col justify-between">
                        <div>
                            <div className="flex justify-between items-start mb-2">
                                <h4 className="text-white font-black text-lg lg:text-xl uppercase tracking-tighter">Order #{o.orderNumber || o.id.substring(0,8)}</h4>
                                <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 border rounded-full lg:rounded-none ${o.status === 'delivered' ? 'text-white border-white bg-white/10' : 'text-zinc-400 border-zinc-600'}`}>{o.status}</span>
                            </div>
                            <p className="text-xs text-zinc-500 font-mono uppercase tracking-widest">{formatDate(o.createdAt)}</p>
                        </div>
                        <div className="mt-4 lg:mt-0">
                            <p className="text-[10px] text-zinc-500 font-black uppercase tracking-widest mb-1">Total</p>
                            <p className="text-white font-black text-2xl tracking-wider">R {o.total?.toFixed(2)}</p>
                        </div>
                    </div>
                </div>
                <button onClick={() => onTrackOrder(o)} className="w-full py-4 rounded-xl lg:rounded-none bg-white text-black text-xs font-black uppercase tracking-[0.2em] hover:bg-zinc-200 transition-colors">Track Order</button>
            </div>
        )) : (<div className="text-center py-32 text-zinc-600 text-xs uppercase font-black tracking-[0.3em] flex flex-col items-center"><Package size={64} strokeWidth={1} className="mb-6"/>No orders yet</div>)}
    </div>
  );

  const renderWishlistView = () => (
      <div className="p-4 lg:p-6 pb-20 lg:pb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {wishlist?.length > 0 ? wishlist.map((item, i) => { 
                const p = products.find(prod => String(prod.id) === String(typeof item === 'string' ? item : item.productId)); 
                return p ? (
                    <div key={i} onClick={() => onSelectProduct(p)} className="aspect-[4/5] bg-zinc-950 rounded-xl lg:rounded-none overflow-hidden border border-zinc-800 relative group cursor-pointer">
                        <img src={p.image} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"/>
                        <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-md p-2 rounded-full lg:rounded-none border border-zinc-700">
                            <Heart size={16} className="text-white fill-white"/>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
                            <p className="text-white font-black text-xs uppercase truncate">{p.name}</p>
                            <p className="text-zinc-400 font-mono text-[10px] uppercase">R {p.price}</p>
                        </div>
                    </div>
                ) : null 
            }) : <div className="col-span-full text-center py-32 text-zinc-600 text-xs uppercase font-black tracking-[0.3em] flex flex-col items-center"><Heart size={64} strokeWidth={1} className="mb-6"/>Wishlist empty</div>}
        </div>
      </div>
  );

  const renderSettingsContent = () => {
    const SettingsHeader = ({ title, onBack }: any) => (
        <div className="p-6 lg:p-10 border-b border-zinc-800 flex items-center gap-6 bg-black sticky top-0 z-10">
            <button onClick={onBack} className="p-3 rounded-full lg:rounded-none bg-zinc-900 border border-zinc-800 hover:bg-white hover:text-black text-zinc-500 transition-colors"><ArrowLeft size={20} /></button>
            <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter">{title}</h2>
        </div>
    );

    if (settingsSubView === 'payment') return (
        <div className="animate-in slide-in-from-right h-full">
            <SettingsHeader title="Wallet & Cards" onBack={() => setSettingsSubView('menu')} />
            <div className="p-6 lg:p-10 max-w-2xl pb-24 lg:pb-10">
                <Wallet profile={profile} />
            </div>
        </div>
    );
    

    if (settingsSubView === 'activity') {
        const getRealDevice = () => {
            const ua = navigator.userAgent;
            let browser = "Web Browser";
            let os = "Unknown Device";
            
            if (ua.includes("Firefox")) browser = "Firefox";
            else if (ua.includes("Edg")) browser = "Edge";
            else if (ua.includes("Chrome")) browser = "Chrome";
            else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";

            if (ua.includes("Win")) os = "Windows";
            else if (ua.includes("Mac")) os = "Mac OS";
            else if (ua.includes("Android")) os = "Android";
            else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";
            else if (ua.includes("Linux")) os = "Linux";

            return `${browser} on ${os}`;
        };

        const currentDeviceName = getRealDevice();
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone.split('/')[1]?.replace('_', ' ') || 'Local Time';

        return (
            <div className="animate-in slide-in-from-right h-full">
                <SettingsHeader title="Session Activity" onBack={() => setSettingsSubView('menu')} />
                <div className="p-6 lg:p-10 max-w-2xl space-y-8 pb-24 lg:pb-10">
                    <div>
                        <h3 className="text-white font-black uppercase tracking-widest text-xs mb-4 border-b border-zinc-800 pb-2">Active Session</h3>
                        <div className="bg-zinc-950 border border-zinc-800 lg:rounded-none rounded-xl p-5 flex justify-between items-start shadow-xl">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-green-500/10 text-green-500 flex items-center justify-center border border-green-500/30">
                                    {currentDeviceName.includes('iOS') || currentDeviceName.includes('Android') ? <Smartphone size={20}/> : <Laptop size={20}/>}
                                </div>
                                <div>
                                    <p className="text-white font-black uppercase tracking-widest text-sm mb-1">{currentDeviceName}</p>
                                    <p className="text-zinc-500 font-mono text-[10px] uppercase">Zone: {timeZone} • Active Now</p>
                                </div>
                            </div>
                            <span className="bg-green-500 text-black text-[9px] font-black uppercase tracking-widest px-2 py-1">Online</span>
                        </div>
                    </div>
                    <div>
                        <h3 className="text-white font-black uppercase tracking-widest text-xs mb-4 border-b border-zinc-800 pb-2">Login History</h3>
                        <div className="text-center py-10 border border-dashed border-zinc-800 lg:rounded-none rounded-xl">
                            <p className="text-zinc-500 font-black uppercase tracking-widest text-xs">No other active sessions detected.</p>
                        </div>
                    </div>
                    <button disabled className="w-full py-5 border border-zinc-800 text-zinc-600 font-black uppercase tracking-widest text-[10px] transition-colors mt-8 opacity-50 cursor-not-allowed">
                        Terminate All Other Sessions
                    </button>
                </div>
            </div>
        );
    }

    if (settingsSubView === 'privacy') return (<div className="animate-in slide-in-from-right h-full"><SettingsHeader title="Privacy Mode" onBack={() => setSettingsSubView('menu')} /><div className="p-6 lg:p-10 space-y-4 max-w-2xl pb-24 lg:pb-10">{['public', 'private', 'ghost'].map((mode: any) => (<button key={mode} onClick={() => { setPendingPrivacy(mode); setSettingsSubView('privacy-confirm'); }} className={`w-full p-6 lg:rounded-none rounded-2xl border transition-colors flex items-center gap-6 text-left ${((profile.isAnonymous && mode==='ghost') || (!profile.isAnonymous && profile.isPrivate && mode==='private') || (!profile.isAnonymous && !profile.isPrivate && mode==='public')) ? 'bg-white text-black border-white' : 'bg-zinc-950 border-zinc-800 text-white hover:border-zinc-500'}`}>{mode === 'public' ? <Globe size={28}/> : mode === 'private' ? <Lock size={28}/> : <Ghost size={28}/>}<div className="flex-1"><h4 className="font-black text-lg uppercase tracking-widest mb-1">{mode}</h4><p className="text-xs font-mono opacity-60 uppercase">{mode === 'ghost' ? 'Invisible on the grid.' : mode === 'private' ? 'Approved eyes only.' : 'Visible to everyone.'}</p></div>{((profile.isAnonymous && mode==='ghost') || (!profile.isAnonymous && profile.isPrivate && mode==='private') || (!profile.isAnonymous && !profile.isPrivate && mode==='public')) && <Check size={24}/>}</button>))}</div></div>);
    if (settingsSubView === 'privacy-confirm' && pendingPrivacy) return (<div className="h-full flex flex-col animate-in slide-in-from-right items-center justify-center text-center p-6"><div className="w-32 h-32 bg-zinc-950 border border-zinc-800 lg:rounded-none rounded-full flex items-center justify-center mb-8">{React.createElement(privacyDetails[pendingPrivacy].icon, { size: 64, className: "text-white" })}</div><h3 className="text-4xl font-black text-white mb-4 uppercase italic tracking-tighter">{privacyDetails[pendingPrivacy].title}</h3><p className="text-zinc-500 font-mono text-sm leading-relaxed mb-12 max-w-sm">{privacyDetails[pendingPrivacy].desc}</p><div className="flex flex-col gap-4 w-full max-w-xs"><button onClick={confirmPrivacyChange} disabled={loading} className="w-full py-5 bg-white text-black font-black text-sm uppercase tracking-[0.2em] lg:rounded-none rounded-xl">{loading ? 'Updating...' : 'Accept Protocol'}</button><button onClick={() => setSettingsSubView('privacy')} className="text-zinc-600 hover:text-white font-bold text-xs uppercase tracking-widest transition-colors py-4">Cancel</button></div></div>);
    if (settingsSubView === 'style') return (<div className="animate-in slide-in-from-right h-full"><SettingsHeader title="Your Vibe" onBack={() => setSettingsSubView('menu')} /><div className="p-6 lg:p-10 max-w-3xl pb-24 lg:pb-10"><div className="flex flex-wrap gap-4 mb-10">{['Streetwear', 'Vintage', 'Y2K', 'Luxury', 'Minimalist', 'Techwear', 'Grunge', 'Casual'].map(s => (<button key={s} onClick={() => selectedStyles.includes(s.toLowerCase()) ? setSelectedStyles(prev => prev.filter(i => i !== s.toLowerCase())) : setSelectedStyles(prev => [...prev, s.toLowerCase()])} className={`px-6 py-3 lg:rounded-none rounded-full border text-xs font-black uppercase tracking-widest transition-all ${selectedStyles.includes(s.toLowerCase()) ? 'bg-white text-black border-white' : 'bg-zinc-950 text-zinc-500 border-zinc-800 hover:border-zinc-500'}`}>{s}</button>))}</div><button onClick={handleSaveStyles} disabled={loading} className="w-full max-w-md bg-white text-black font-black text-xs uppercase tracking-[0.2em] py-5 lg:rounded-none rounded-xl hover:bg-zinc-200 transition-colors">{loading ? 'Saving...' : 'Save Preferences'}</button></div></div>);
    if (settingsSubView === 'phone' || settingsSubView === 'email') { const isPhone = settingsSubView === 'phone'; return (<div className="animate-in slide-in-from-right h-full"><SettingsHeader title={isPhone ? 'Change Number' : 'Change Email'} onBack={() => setSettingsSubView('menu')} /><div className="p-6 lg:p-10 max-w-md pb-24 lg:pb-10"><input autoFocus value={tempInput} onChange={(e) => setTempInput(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 p-5 lg:rounded-none rounded-xl text-white mb-6 outline-none focus:border-white transition-colors font-mono" placeholder={isPhone ? "+27..." : "email@domain.com"} type={isPhone ? "tel" : "email"} /><button onClick={() => handleSaveSetting(isPhone ? 'phoneNumber' : 'email')} disabled={loading} className="w-full bg-white text-black font-black text-xs uppercase tracking-[0.2em] py-5 lg:rounded-none rounded-xl hover:bg-zinc-200 transition-colors">{loading ? 'Saving...' : 'Confirm Update'}</button></div></div>); }
    if (settingsSubView === 'blocked') return (<div className="animate-in slide-in-from-right h-full"><SettingsHeader title="Blocked Entities" onBack={() => setSettingsSubView('menu')} /><div className="p-6 lg:p-10 max-w-2xl space-y-4 pb-24 lg:pb-10">{blockedList.length > 0 ? blockedList.map(u => (<div key={u.uid} className="flex items-center justify-between p-6 bg-zinc-950 border border-zinc-800 lg:rounded-none rounded-2xl"><div className="flex items-center gap-4"><div className="w-12 h-12 lg:rounded-none rounded-full bg-black border border-zinc-800 flex items-center justify-center text-zinc-600 overflow-hidden">{u.photoURL ? <img src={u.photoURL} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale"/> : <User size={20}/>}</div><div><p className="text-white font-black text-sm uppercase tracking-widest mb-1">{u.displayName}</p><p className="text-zinc-500 text-[10px] font-mono uppercase">Blocked</p></div></div><button onClick={() => handleUnblock(u.uid)} className="px-6 py-3 bg-white text-black text-[10px] font-black uppercase tracking-widest lg:rounded-none rounded-lg hover:bg-zinc-200 transition-colors">Unblock</button></div>)) : (<div className="text-center py-32 text-zinc-600 text-xs font-black uppercase tracking-[0.3em]">No blocked entities</div>)}</div></div>);

    return (
        <div className="pb-24 lg:pb-10 animate-in slide-in-from-left w-full h-full">
          <SettingsHeader title="Settings" onBack={() => setActiveView('closet')} />
          <div className="max-w-3xl mx-auto w-full pt-2">
            <SectionTitle title="Account Settings" />
            <div className="bg-zinc-950 lg:border-y border-zinc-800 lg:rounded-none rounded-2xl overflow-hidden mx-4 lg:mx-0">
              <SettingsRow icon={Coins} label="Buy Credits" value={`${profile?.cred || (profile as any)?.credits || (profile as any)?.balance || 0} CR`} onClick={() => setIsWalletOpen(true)} />
              <SettingsRow icon={Edit3} label="Edit Profile" onClick={onEditProfile} />
              <SettingsRow icon={Palette} label="Style Preferences" value={selectedStyles[0] || "Not set"} onClick={() => setSettingsSubView('style')} />
              <SettingsRow icon={Smartphone} label="Change Number" value={profile?.phoneNumber || "Not Set"} onClick={() => { setTempInput(profile?.phoneNumber || ''); setSettingsSubView('phone'); }} />
              <SettingsRow icon={Mail} label="Change Email" value={profile?.email || "Not set"} onClick={() => { setTempInput(profile?.email || ''); setSettingsSubView('email'); }} />
              <SettingsRow icon={Lock} label="Change Password" onClick={handlePasswordReset} />
              <SettingsRow icon={Activity} label="Your Activity" onClick={() => setSettingsSubView('activity')} />
            </div>
            
            <SectionTitle title="Privacy & Safety" />
            <div className="bg-zinc-950 lg:border-y border-zinc-800 lg:rounded-none rounded-2xl overflow-hidden mx-4 lg:mx-0">
              <SettingsRow icon={getCurrentLevel() === 'ghost' ? Ghost : getCurrentLevel() === 'private' ? Lock : Globe} label="Account Privacy" value={getCurrentLevel() === 'ghost' ? 'Ghost Mode' : getCurrentLevel() === 'private' ? 'Private' : 'Public'} onClick={() => setSettingsSubView('privacy')} />
              <SettingsRow icon={Users} label="Blocked Users" value={String((profile.blockedUsers || []).length)} onClick={() => setSettingsSubView('blocked')} />
            </div>
            
            <SectionTitle title="Share & Earn" />
            <div className="bg-zinc-950 lg:border-y border-zinc-800 lg:rounded-none rounded-2xl overflow-hidden mx-4 lg:mx-0">
              
              <SettingsRow 
                icon={Target} 
                label="Affiliate & Influencer Program: Join to Earn" 
                onClick={() => setIsAffiliateOpen(true)} // <-- THIS IS THE FIX
              />

              <SettingsRow 
                icon={QrCode} 
                label="Share Profile" 
                onClick={onInviteFriends} 
              />
              
            </div>
            
            <SectionTitle title="Support Protocol" />
            <div className="bg-zinc-950 lg:border-y border-zinc-800 lg:rounded-none rounded-2xl overflow-hidden mx-4 lg:mx-0">
            <SettingsRow 
                icon={Flag} 
                label="Report a Problem" 
                onClick={() => window.open('https://scoophype.co.za/support', '_blank')} 
            />
            <SettingsRow 
                icon={HelpCircle} 
                label="Terms" 
                onClick={() => window.open('https://scoophype.co.za/Terms', '_blank')} 
            />
            <SettingsRow 
                icon={Shield} 
                label="Privacy Policy" 
                onClick={() => window.open('https://scoophype.co.za/privacy', '_blank')} 
            />
            </div>
            
            <div className="mx-4 lg:mx-0 mt-10 mb-10">
                <button onClick={onSignOut} className="w-full p-5 lg:rounded-none rounded-2xl bg-red-950/20 border border-red-900/50 text-red-500 font-black uppercase tracking-widest text-xs flex items-center justify-center gap-3 hover:bg-red-900/40 transition-colors">
                    <LogOut size={16} /> Logout
                </button>
            </div>
          </div>
        </div>
    );
  };

  const renderUserListView = (title: string, users: any[], brandObjects?: any[]) => {
    const filteredUsers = users.filter(u => (u.displayName || '').toLowerCase().includes(listSearchTerm.toLowerCase()));
    
    const filteredBrands = brandObjects ? brandObjects.filter(b => 
        (b.displayName || b.name || b.businessName || b.uid || '').toLowerCase().includes(listSearchTerm.toLowerCase())
    ) : [];
    
    if (loadingList) return <div className="p-32 flex justify-center"><Loader2 className="animate-spin text-zinc-500" size={32}/></div>;
    
    return (
        <div className="p-6 lg:p-12 animate-in slide-in-from-right max-w-4xl mx-auto pb-24 lg:pb-12">
            <div className="flex justify-between items-center mb-8 border-b border-zinc-800 pb-6">
                <h2 className="text-3xl font-black uppercase tracking-tighter italic text-white">{title}</h2>
                <button onClick={() => setActiveView('closet')} className="p-3 bg-zinc-900 border border-zinc-800 rounded-full hover:bg-white hover:text-black text-zinc-500 transition-colors"><X size={20}/></button>
            </div>
            
            <div className="relative mb-10">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <input className="w-full bg-zinc-950 border border-zinc-800 rounded-full lg:rounded-none py-4 pl-14 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-white transition-colors uppercase font-mono" placeholder={`SEARCH ${title.toUpperCase()}...`} value={listSearchTerm} onChange={(e) => setListSearchTerm(e.target.value)} />
            </div>
            
            {brandObjects && filteredBrands.length > 0 && (
                <div className="mb-10 border-b border-zinc-800 pb-10">
                    <h3 className="text-zinc-500 text-xs uppercase font-black tracking-widest mb-6">Brands</h3>
                    <div className="grid grid-cols-3 lg:grid-cols-5 gap-6">
                        {filteredBrands.map(b => {
                            const brandName = b.displayName || b.name || b.businessName || b.uid;
                            const brandImage = b.photoURL || b.logo || b.avatar;
                            return (
                                <div key={b.uid} onClick={() => onViewBrand && onViewBrand(brandName)} className="flex flex-col items-center gap-3 cursor-pointer group">
                                    <div className="w-20 h-20 rounded-full lg:rounded-none bg-zinc-950 border border-zinc-800 overflow-hidden flex items-center justify-center group-hover:border-white transition-colors">
                                        {brandImage ? (
                                            <img src={brandImage} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"/>
                                        ) : (
                                            <span className="text-zinc-500 font-black text-2xl italic group-hover:text-white transition-colors">{brandName.charAt(0)}</span>
                                        )}
                                    </div>
                                    <p className="text-[10px] text-zinc-400 font-black uppercase truncate w-full text-center group-hover:text-white transition-colors">{brandName}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            
            <div>
                {brandObjects && <h3 className="text-zinc-500 text-xs uppercase font-black tracking-widest mb-6">People</h3>}
                {filteredUsers.length > 0 ? (
                    <div className="space-y-4">
                        {filteredUsers.map(u => {
                            const isGhost = u.isAnonymous;
                            const isPrivate = u.isPrivate && !isGhost;
                            const display = isGhost ? 'Anonymous' : u.displayName;
                            const handle = isGhost ? 'HIDDEN' : u.handle || u.username || 'user';
                            
                            return (
                                <div 
                                    key={u.uid} 
                                    onClick={() => {
                                        if (!isGhost) {
                                            if (onViewProfile) {
                                                onViewProfile(u.uid);
                                            } else {
                                                navigate(`/profile/${u.uid}`);
                                            }
                                        }
                                    }} 
                                    className={`flex items-center gap-6 p-4 bg-zinc-950 border border-zinc-800 rounded-2xl lg:rounded-none transition-colors group ${
                                        !isGhost ? 'cursor-pointer hover:border-white' : 'cursor-default opacity-50'
                                    }`}
                                >
                                    <div className="w-14 h-14 rounded-full lg:rounded-none bg-black border border-zinc-800 overflow-hidden shrink-0 flex items-center justify-center">
                                        {!isGhost && u.photoURL ? (
                                            <img src={u.photoURL} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"/>
                                        ) : (
                                            isGhost ? <Ghost size={20} className="text-zinc-600" /> : <User size={20} className="text-zinc-600" />
                                        )}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <p className="text-white font-black text-base uppercase tracking-widest">{display}</p>
                                            {isPrivate && <Lock size={14} className="text-zinc-500" />}
                                        </div>
                                        <p className="text-zinc-500 font-mono text-[10px] uppercase">@{handle}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-20 text-zinc-600 text-xs uppercase font-black tracking-[0.3em]">List empty</div>
                )}
            </div>
        </div>
    );
  };

  const displayName = profile?.displayName || 'User';
  const bio = profile?.bio || 'Welcome to Scoop!';
  const photoURL = profile?.avatar || profile?.photoURL || ''; 
  const credits = profile?.cred || (profile as any)?.credits || (profile as any)?.balance || 0; 
  const followersCount = (profile.followers || []).length;
  const followingCount = (followingBrands?.length || 0) + ((profile.followingUsers || []).length);

  return (
    <div className="w-full min-h-screen lg:min-h-0 lg:h-[calc(100vh-160px)] flex flex-col lg:flex-row bg-black relative">
      <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />

      <PostDetailModal 
        post={selectedPost} 
        isOpen={!!selectedPost} 
        onClose={() => setSelectedPost(null)}
        onSelectProduct={onSelectProduct}
        currentUser={profile}
        onSave={handleSaveItem}
        allPosts={myPosts}
      />

      <div className={`w-full lg:w-[400px] xl:w-[450px] shrink-0 bg-zinc-950 lg:border-r border-zinc-800 transition-all duration-300 lg:h-full overflow-y-auto scrollbar-hide flex-col ${activeView === 'settings' ? 'hidden lg:flex' : 'flex'}`}>
          <div className="flex-1 p-6 lg:p-10 pt-10">
              
              <div className="relative mb-8 w-fit mx-auto lg:mx-0">
                  <div onClick={handleImageClick} className="w-32 h-32 lg:w-48 lg:h-48 rounded-full lg:rounded-none border-2 border-zinc-800 flex items-center justify-center text-zinc-600 text-5xl font-black overflow-hidden bg-black cursor-pointer group shadow-2xl relative">
                      {photoURL ? <img src={photoURL} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" /> : displayName.charAt(0)}
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 backdrop-blur-sm">
                          {loading ? <Loader2 size={24} className="text-white animate-spin" /> : <Edit3 size={24} className="text-white" />}
                      </div>
                  </div>
              </div>

              <div className="text-center lg:text-left mb-10">
                  <div className="flex items-center justify-center lg:justify-start gap-3 mb-2">
                      <h3 className="text-3xl lg:text-5xl font-black text-white italic tracking-tighter uppercase">{displayName}</h3>
                      {profile.isAnonymous && <Ghost size={20} className="text-zinc-500" />}
                      {!profile.isAnonymous && profile.isPrivate && <Lock size={20} className="text-zinc-500" />}
                  </div>
                  <p className="text-zinc-400 font-mono text-xs uppercase tracking-widest leading-relaxed max-w-xs mx-auto lg:mx-0">{bio}</p>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-10 border-y border-zinc-800 py-6">
                  <button onClick={() => setActiveView('followers')} className="text-center hover:opacity-70 transition-opacity">
                      <div className="text-2xl font-black text-white mb-1">{followersCount}</div>
                      <div className="text-zinc-500 text-[9px] uppercase font-black tracking-widest">Followers</div>
                  </button>
                  <button onClick={() => setActiveView('following')} className="text-center hover:opacity-70 transition-opacity border-x border-zinc-800">
                      <div className="text-2xl font-black text-white mb-1">{followingCount}</div>
                      <div className="text-zinc-500 text-[9px] uppercase font-black tracking-widest">Following</div>
                  </button>

                  <button onClick={() => setIsWalletOpen(true)} className="text-center hover:opacity-70 transition-opacity">
                      <div className="text-2xl font-black text-white mb-1">{credits}</div>
                      <div className="text-zinc-500 text-[9px] uppercase font-black tracking-widest flex items-center justify-center gap-1">
                          <Coins size={10}/> Wallet
                      </div>
                  </button>
              </div>

              <div className="flex flex-col gap-3">
                  <button onClick={onEditProfile} className="w-full py-4 lg:rounded-none rounded-xl bg-white text-black font-black text-xs uppercase tracking-[0.2em] hover:bg-zinc-200 transition-colors">
                      Edit Profile
                  </button>
                  <button onClick={() => setActiveView('settings')} className="w-full py-4 lg:rounded-none rounded-xl bg-black border border-zinc-800 text-white font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:border-white transition-colors">
                      <Settings size={16} /> Settings
                  </button>
              </div>
          </div>
          
          <div className="lg:hidden flex border-t border-zinc-800 bg-black sticky bottom-0 z-10 overflow-x-auto scrollbar-hide">
            {['closet', 'saved', 'wishlist', 'orders'].map((tab) => (
                <button key={tab} onClick={() => setActiveView(tab as any)} className={`min-w-[80px] flex-1 py-5 text-[10px] font-black uppercase tracking-widest transition-colors ${activeView === tab ? 'border-t-2 border-white text-white bg-zinc-900/50' : 'text-zinc-600 hover:text-white'}`}>
                    <div className="flex items-center justify-center gap-2">
                        {tab === 'closet' && <Grid size={16} />}
                        {tab === 'saved' && <Bookmark size={16} />}
                        {tab === 'wishlist' && <Heart size={16} />}
                        {tab === 'orders' && <Package size={16} />}
                    </div>
                </button>
            ))}
          </div>
      </div>

      <div className="flex-1 flex flex-col bg-black relative lg:h-full overflow-y-auto scrollbar-hide pb-16 lg:pb-0">
          
          <div className="hidden lg:flex border-b border-zinc-800 bg-zinc-950/90 backdrop-blur-md sticky top-0 z-20 shrink-0">
            {['closet', 'saved', 'wishlist', 'orders'].map((tab) => (
                <button key={tab} onClick={() => { setActiveView(tab as any); setSettingsSubView('menu'); }} className={`flex-1 py-6 text-xs font-black uppercase tracking-[0.2em] transition-all relative ${activeView === tab ? 'text-white' : 'text-zinc-600 hover:text-white hover:bg-zinc-900/50'}`}>
                    <div className="flex items-center justify-center gap-3">
                        {tab === 'closet' && <Grid size={16} />}
                        {tab === 'saved' && <Bookmark size={16} />}
                        {tab === 'wishlist' && <Heart size={16} />}
                        {tab === 'orders' && <Package size={16} />}
                        {tab}
                    </div>
                    {activeView === tab && <div className="absolute bottom-0 left-0 w-full h-[2px] bg-white animate-in zoom-in" />}
                </button>
            ))}
          </div>

          <div className="flex-1">
              {activeView === 'closet' && renderClosetView()}
              {activeView === 'saved' && renderSavedPostsView()} 
              {activeView === 'followers' && renderUserListView('Followers', followersList)}
              {activeView === 'following' && renderUserListView('Following', followingUsersList, followingBrandsList)}
              {activeView === 'wishlist' && renderWishlistView()}
              {activeView === 'orders' && renderOrdersView()}
              {activeView === 'settings' && renderSettingsContent()}
          </div>
      </div>

      <WalletTerminal 
        isOpen={isWalletOpen} 
        onClose={() => setIsWalletOpen(false)} 
        currentUser={profile} 
        />

        <AffiliateModal 
        isOpen={isAffiliateOpen} 
        onClose={() => setIsAffiliateOpen(false)} 
        profile={profile}
        onOpenVault={() => setIsWalletOpen(true)}
        onOpenStore={() => {}}
        />

    </div>
  );
};